import { moneyFormat } from '../../../../utils/appUtils';
import type { CoverageStatus } from './coverage.types';
import { formatCoverageDate } from './coverageDates';

/**
 * Display helpers for the coverage tile.
 *
 * One rule runs through all of them: absence renders as absence. The prototype
 * coerced missing values into confident ones -- `daysToExpiry ?? 0` printed
 * "0 days" in critical red on a quote that simply had no expiry -- which is a
 * deadline invented out of nothing on the card whose whole job is urgency.
 */

/** Shown wherever a value is genuinely not there. */
export const ABSENT = '—';

export const money = (value: number | undefined): string =>
  value === undefined ? ABSENT : moneyFormat(value);

export const date = (iso: string | undefined): string =>
  formatCoverageDate(iso) ?? ABSENT;

/** "in 12 days" / "today" / "9 days ago", or nothing at all. */
export function relativeDays(days: number | undefined): string | undefined {
  if (days === undefined) {
    return undefined;
  }

  if (days === 0) {
    return 'today';
  }

  const magnitude = Math.abs(days);
  const unit = magnitude === 1 ? 'day' : 'days';

  return days > 0 ? `in ${days} ${unit}` : `${magnitude} ${unit} ago`;
}

export type CoverageTone = 'positive' | 'caution' | 'critical' | 'neutral';

const STATUS_TONE: Record<CoverageStatus, CoverageTone> = {
  inForce: 'positive',
  issued: 'neutral',
  bound: 'caution',
  cancelPending: 'caution',
  expired: 'critical',
  cancelled: 'critical',
  quoteReady: 'caution',
  quoteBlocked: 'caution',
  quoteExpired: 'neutral',
  unknown: 'neutral',
};

export const toneFor = (status: CoverageStatus): CoverageTone =>
  STATUS_TONE[status] ?? 'neutral';
